import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Phone, Mail, MapPin, MessageCircle, Clock, ArrowUpRight, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import odooApi, { buildPhoneHref, buildWhatsAppUrl, getDisplayPhoneNumber } from "../services/odooApi";
import { useLanguage } from "../contexts/LanguageContext";

const buildDefaultFormData = (prefilledEmail = "") => ({
  email: prefilledEmail,
  title: "",
  note: "",
});

export const ContactPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState(() => buildDefaultFormData(user?.email || ""));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    odooApi.getCmsConfig().then((cmsConfig) => {
      if (active && cmsConfig) {
        setConfig(cmsConfig);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      email: user?.email || "",
    }));
  }, [user?.email]);

  const phone = getDisplayPhoneNumber(config?.phone);
  const whatsapp = config?.whatsapp || phone;
  const contactEmail = config?.email || "info@velarisroyal.ae";
  const address = config?.address || "Business Bay, Sheikh Zayed Road\nDubai, United Arab Emirates";
  const workingHours = config?.working_hours || "24/7 Available";
  const whatsappUrl = buildWhatsAppUrl(
    whatsapp,
    t("contact.whatsappPrefill", "Hi! I'd like to inquire about your car rental services."),
  );
  const phoneUrl = buildPhoneHref(phone);
  const qrValue = process.env.REACT_APP_CONTACT_QR_VALUE || whatsappUrl;

  const quickActions = [
    {
      title: t("contact.whatsapp", "WhatsApp"),
      description: t("contact.instantResponse", "Get instant response via WhatsApp"),
      value: whatsapp,
      href: whatsappUrl,
      clickType: "whatsapp",
      icon: MessageCircle,
      accent: "bg-[#25D366]",
      accentSoft: "from-[#25D366]/15 to-[#25D366]/5",
    },
    {
      title: t("contact.phone", "Phone"),
      description: t("contact.callSupport", "Speak directly with our rental advisors"),
      value: phone,
      href: phoneUrl,
      clickType: "call",
      icon: Phone,
      accent: "bg-[#D4AF37]",
      accentSoft: "from-[#D4AF37]/20 to-[#D4AF37]/5",
    },
    {
      title: t("contact.email", "Email"),
      description: t("contact.emailReply", "Best for detailed requests and partnership inquiries"),
      value: contactEmail,
      href: `mailto:${contactEmail}`,
      clickType: null,
      icon: Mail,
      accent: "bg-gray-900 dark:bg-gray-100",
      accentSoft: "from-gray-900/15 to-gray-900/5 dark:from-white/20 dark:to-white/5",
    },
  ].filter((action) => action.href !== '#');

  const detailCards = [
    {
      title: t("contact.address", "Address"),
      value: address,
      icon: MapPin,
    },
    {
      title: t("contact.hours", "Working Hours"),
      value: workingHours,
      icon: Clock,
    },
  ];

  const handleContactClick = (type) => {
    odooApi.trackClick({ click_type: type });
  };

  const handleChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email.trim() || !formData.title.trim() || !formData.note.trim()) {
      toast.error(t("auth.fillAllFields", "Please fill all fields"));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await odooApi.createContactMessage({
        email: formData.email.trim(),
        title: formData.title.trim(),
        note: formData.note.trim(),
        source: "web",
      });

      if (response?.success === false) {
        throw new Error(response?.error || t("contact.submitError", "We couldn't send your message. Please try again."));
      }

      setFormData(buildDefaultFormData(user?.email || ""));
      toast.success(t("contact.messageSent", "Message sent successfully! We'll get back to you soon."));
    } catch (error) {
      toast.error(error.message || t("contact.submitError", "We couldn't send your message. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={t("contact.pageTitle", "Contact Us | Get in Touch for Car Rental")}
        description={t("contact.pageDescription", "Contact Velaris Royal Car Rental for luxury car rentals in Dubai and across the UAE. Reach us by phone, WhatsApp, email, or the contact form.")}
        keywords="contact velaris royal, car rental phone number dubai, whatsapp car rental, luxury car rental contact"
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("nav.contact", "Contact") },
        ]}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="contact-page">
        <section className="relative overflow-hidden bg-black">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(212,175,55,0.26),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(37,211,102,0.18),_transparent_28%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:py-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
                <ShieldCheck className="h-4 w-4" />
                {t("contact.subtitle", "Get in Touch")}
              </div>
              <h1 className="mt-6 font-[var(--font-display)] text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                {t("contact.heading", "Talk to Velaris Royal in the way that suits you best.")}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-white/70 sm:text-lg">
                {t("contact.description", "Have questions? We're here to help. Reach out to us through any of the channels below.")}
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {detailCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.title}
                      className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#D4AF37] text-black">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-white/45">{card.title}</p>
                          <p className="mt-1 whitespace-pre-line text-sm font-medium text-white/85">{card.value}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <a
                    key={action.title}
                    href={action.href}
                    target={action.href.startsWith("http") ? "_blank" : undefined}
                    rel={action.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    onClick={action.clickType ? () => handleContactClick(action.clickType) : undefined}
                    className={`group rounded-3xl border border-white/10 bg-gradient-to-br ${action.accentSoft} p-5 transition-transform hover:-translate-y-1`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-white ${action.accent}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-white">{action.title}</p>
                            <p className="mt-1 text-sm leading-6 text-white/65">{action.description}</p>
                          </div>
                          <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-white/45 transition-colors group-hover:text-[#D4AF37]" />
                        </div>
                        <p className="mt-4 truncate text-sm font-medium text-white/85">{action.value}</p>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-16">
          <div className="rounded-[2rem] border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
                {t("contact.quickContact", "Quick Contact")}
              </p>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                {t("contact.info", "Contact Information")}
              </h2>
              <p className="mt-3 text-sm leading-7 text-gray-600 dark:text-gray-400">
                {t("contact.altMethods", "Use the contact form for detailed requests, or jump straight to WhatsApp, phone, or email if you need a faster reply.")}
              </p>
            </div>

            <div className="space-y-5">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <a
                    key={`secondary-${action.title}`}
                    href={action.href}
                    target={action.href.startsWith("http") ? "_blank" : undefined}
                    rel={action.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-4 rounded-3xl border border-gray-200 p-4 transition-colors hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 dark:border-gray-800 dark:hover:bg-[#D4AF37]/10"
                  >
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-white ${action.accent}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white">{action.title}</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">{action.description}</p>
                      <p className="mt-3 text-sm font-medium text-[#D4AF37]">{action.value}</p>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          <div className="rounded-[2rem] border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
                {t("contact.formEyebrow", "Send a Message")}
              </p>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                {t("contact.sendMessage", "Send us a Message")}
              </h2>
              <p className="mt-3 text-sm leading-7 text-gray-600 dark:text-gray-400">
                {t("contact.formDescription", "Fill out the form below and we'll get back to you as soon as possible.")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                  {t("form.email", "Email Address")} *
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  value={formData.email}
                  onChange={(event) => handleChange("email", event.target.value)}
                  placeholder={t("auth.exampleEmail", "you@example.com")}
                  required
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label htmlFor="contact-title" className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                  {t("form.title", "Title")} *
                </label>
                <Input
                  id="contact-title"
                  value={formData.title}
                  onChange={(event) => handleChange("title", event.target.value)}
                  placeholder={t("contact.exampleTitle", "Weekend rental question")}
                  required
                  data-testid="contact-title"
                />
              </div>

              <div>
                <label htmlFor="contact-note" className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                  {t("form.note", "Note")} *
                </label>
                <Textarea
                  id="contact-note"
                  value={formData.note}
                  onChange={(event) => handleChange("note", event.target.value)}
                  placeholder={t("form.messagePlaceholder", "Tell us how we can help you...")}
                  rows={7}
                  required
                  data-testid="contact-note"
                />
              </div>

              <div className="flex flex-col gap-4 border-t border-gray-200 pt-6 dark:border-gray-800 md:flex-row md:items-center md:justify-between">
                <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
                  {t("contact.formTrust", "We typically reply within a few business hours. For urgent requests, WhatsApp is the fastest channel.")}
                </p>
                <Button
                  type="submit"
                  className="btn-gold min-w-[13rem] text-black"
                  data-testid="contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("contact.sending", "Sending...") : t("btn.sendMessage", "Send Message")}
                </Button>
              </div>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="grid gap-6 overflow-hidden rounded-[2rem] border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900 md:grid-cols-[0.95fr_0.45fr] md:items-center md:p-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
                {t("contact.qrEyebrow", "Scan & Connect")}
              </p>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                {t("contact.qrTitle", "Save our details in one scan")}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-gray-600 dark:text-gray-400">
                {t("contact.qrDescription", "Scan this QR code to open our preferred contact channel instantly on your phone. Replace the configured QR value later if you want it to point to a landing page or downloadable vCard.")}
              </p>
              <div className="mt-5 inline-flex max-w-full items-center rounded-full border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-4 py-2 text-sm font-medium text-[#8C6B14] dark:text-[#E8C868]">
                <span>{t("contact.qrHint", "Current QR destination")}:</span>
                <span className="ml-2 max-w-[18rem] truncate text-gray-700 dark:text-gray-200">{qrValue}</span>
              </div>
            </div>

            <div className="justify-self-start md:justify-self-end">
              <div className="rounded-[2rem] border border-gray-200 bg-gray-50 p-5 shadow-inner dark:border-gray-800 dark:bg-black/40">
                <div className="rounded-[1.5rem] bg-white p-4">
                  <QRCode
                    value={qrValue}
                    size={176}
                    style={{ height: "auto", width: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8 lg:pb-20">
          <div className="overflow-hidden rounded-[2rem] border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="border-b border-gray-200 p-8 dark:border-gray-800 lg:border-b-0 lg:border-r">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
                  {t("contact.mapEyebrow", "Visit Us")}
                </p>
                <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                  {t("contact.mapTitle", "Drop by our Dubai base")}
                </h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-600 dark:text-gray-400">
                  {address}
                </p>
                <a
                  href={phoneUrl}
                  onClick={() => handleContactClick('call')}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-[#C5A028]"
                >
                  {t("btn.callNow", "Call Now")}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
              <div className="h-[360px] bg-gray-200 dark:bg-gray-800">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d462560.6828124055!2d54.947286815820544!3d25.075384839671!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e5084295162!2sDubai%20-%20United%20Arab%20Emirates!5e0!3m2!1sen!2s!4v1704000000000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t("contact.locationTitle", "Velaris Royal Location")}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
